
/**
 * Module dependencies.
 */

import { createAction } from 'redux-actions'
import request from 'superagent'
import { navigate } from '@segment/deku-enroute'
import isEmpty from 'is-empty'
import { getMetadataByTypeAndSlug, getMetadataRequest } from 'selectors'
import fetch from 'lib/fetch'

/**
 * Action types.
 */

export const REQUEST_METADATA_BY_SLUG = 'REQUEST_METADATA_BY_SLUG'
export const RECEIVE_METADATA_BY_SLUG = 'RECEIVE_METADATA_BY_SLUG'
export const FETCH_METADATA_BY_SLUG_ERROR = 'FETCH_METADATA_BY_SLUG_ERROR'
export const INVALIDATE_METADATA_BY_SLUG = 'INVALIDATE_METADATA_BY_SLUG'
export const UPDATE_METADATA_BY_SLUG_ERROR = 'UPDATE_METADATA_BY_SLUG_ERROR'
export const UPDATED_METADATA_BY_SLUG = 'UPDATED_METADATA_BY_SLUG'
export const CREATE_METADATA = 'CREATE_METADATA'
export const CREATED_METADATA = 'CREATED_METADATA'
export const CREATE_METADATA_ERROR = 'CREATE_METADATA_ERROR'

/**
 * Actions.
 */

const requestMetadataBySlug = createAction(REQUEST_METADATA_BY_SLUG)
const fetchMetadataBySlugError = createAction(FETCH_METADATA_BY_SLUG_ERROR)
const receiveMetadataBySlug = createAction(RECEIVE_METADATA_BY_SLUG)
const createMetadataError = createAction(CREATE_METADATA_ERROR)
const createdMetadata = createAction(CREATED_METADATA)
const updateMetadataBySlugError = createAction(UPDATE_METADATA_BY_SLUG_ERROR)
const updatedMetadataBySlug = createAction(UPDATED_METADATA_BY_SLUG)

/**
 * Invalidate the metadata `metadataType` and `slug`.
 */

export const invalidateMetadataBySlug = createAction(INVALIDATE_METADATA_BY_SLUG)

/**
 * Fetch metadata `metadataType` and `slug` (if needed).
 */

export function fetchMetadataBySlugIfNeeded ({ metadataType, slug }) {
  return function (dispatch, getState) {
    const state = getState()
    if (shouldFetchMetadataBySlug(metadataType, slug, state)) {
      return dispatch(fetchMetadataBySlug({ metadataType, slug }))
    }
  }
}

/**
 * Fetch all metadatas for `metadataType`.
 */

export const REQUEST_METADATAS = 'REQUEST_METADATAS'
export const RECEIVE_METADATAS = 'RECEIVE_METADATAS'
export const FETCH_METADATAS_ERROR = 'FETCH_METADATAS_ERROR'

export function fetchMetadatas ({ metadataType }) {
  return {
    types: {
      request: REQUEST_METADATAS,
      failure: FETCH_METADATAS_ERROR,
      receive: RECEIVE_METADATAS
    },
    shouldCallAPI: state => {
      const { isRequesting, data } = getMetadataRequest(state, metadataType)
      return !isRequesting && isEmpty(data)
    },
    callAPI: () => fetch(`/api/metadata/${metadataType}`).then(res => {
      if (!res.ok) {
        const err = new Error(`Unable to fetch ${metadataType} metadata`)
        err.status = res.status
        throw err
      }
      return res.json()
    }),
    payload: { metadataType }
  }
}

/**
 * Create a new metadata using the given `metadataType` and `data`.
 */

export function createMetadata ({ metadataType, data }) {
  return function (dispatch) {
    dispatch({ metadataType, type: CREATE_METADATA, payload: { data } })

    let json = null
    try {
      json = JSON.parse(data)
    } catch (e) {
      const error = new Error(`Unable to parse ${metadataType} JSON: ${e.message}`)
      return dispatch(createMetadataError({ metadataType, error }))
    }

    // db-service is unable to parse dates for some reason?
    if (json.createdAt) {
      delete json.createdAt
    }

    fetch(`/api/metadata/${metadataType}`, {
      method: 'POST',
      body: JSON.stringify(json)
    }).then(res => {
      if (!res.ok) {
        throw new Error(`Unable to create ${metadataType} (${res.status})`)
      }

      return res.json()
    }).then(body => {
      // navigate to the new partner
      navigate(`/partner-portal/${metadataType}/${body.slug}/raw`)
      dispatch(createdMetadata({ metadataType, data }))
    }).catch(error => {
      dispatch(createMetadataError({ metadataType, error }))
    })
  }
}

/**
 * Invalidate the metadata `metadataType` and `slug` with `data`.
 */

export function updateMetadataBySlug ({ metadataType, slug, data }) {
  return function (dispatch) {
    let metadata = null

    // when submitting a "raw" metadata, it'll be a string
    if (typeof data === 'string') {
      try {
        metadata = JSON.parse(data)
      } catch (e) {
        const error = new Error(`Unable to parse ${metadataType} JSON: ${e.message}`)
        return dispatch(updateMetadataBySlugError({ metadataType, error }))
      }
    } else {
      metadata = data
    }

    // fixme todo
    if (metadataType === 'bitool') {
      // hack for different schema structure
      if (metadata.logos) {
        metadata.logo = metadata.logos.default
        delete metadata.logos
      }

      // hack for CreatedAt parsing
      if (metadata.createdAt) {
        delete metadata.createdAt
      }
    }

    dispatch(invalidateMetadataBySlug({ metadataType, slug }))

    fetch(`/api/metadata/${metadataType}/${slug}`, {
      method: 'PATCH',
      body: JSON.stringify(metadata)
    })
    .then(res => {
      if (!res.ok) {
        throw new Error(`Unable to update "${metadataType}/${slug}"`)
      }

      return res.json()
    }).then(data => {
      dispatch(updatedMetadataBySlug({ metadataType, slug, data }))
    }).catch(error => {
      dispatch(updateMetadataBySlugError({ metadataType, slug, error }))
    })
  }
}

/**
 * Should we fetch the partner metadata `metadataType` and `slug`?
 */

function shouldFetchMetadataBySlug (metadataType, slug, state) {
  const { data } = getMetadataByTypeAndSlug(state, metadataType, slug) || {}
  if (!data) return true
  if (data.isRequesting) return false
  return data.didInvalidate
}

/**
 * Fetch partner metadata `metadataType` and `slug`.
 */

function fetchMetadataBySlug ({ metadataType, slug }) {
  return function (dispatch) {
    dispatch(requestMetadataBySlug({ metadataType, slug }))
    request
    .get(`/api/metadata/${metadataType}/${slug}`)
    .end(function (err, res) {
      if (err || !res.ok) {
        const error = err || new Error(`Unable to fetch metadata "${metadataType}/${slug}"`)
        dispatch(fetchMetadataBySlugError({ metadataType, slug, error }))
      } else {
        dispatch(receiveMetadataBySlug({ metadataType, slug, data: res.body }))
      }
    })
  }
}

export function fetchAllMetadatas () {
  return function (dispatch, getState) {
    const state = getState()
    const types = Object.keys(state.metadatasBySlug)
    for (const metadataType of types) {
      dispatch(fetchMetadatas({ metadataType }))
    }
  }
}



// WEBPACK FOOTER //
// ./actions/metadata.js